import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Search, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from './HelpHint';
import { api } from '../lib/api';

/**
 * What "running low" means, per item.
 *
 * ## Why this is a screen rather than a seeded table
 *
 * The obvious shortcut is to ship sensible-looking defaults — ONT at ten,
 * drop fibre at five hundred metres. It is the wrong shortcut. Those numbers
 * are a statement about how fast this particular business gets through stock
 * and how long its supplier takes to deliver, and nobody writing the software
 * knows either. Seeded guesses would be wrong, would look authoritative
 * because the system produced them, and would be adjusted by nobody.
 *
 * So the table starts empty and this is where an admin fills it in. An item
 * with no rule is simply never reported as low, which is the honest behaviour
 * for something nobody has expressed an opinion about.
 *
 * ## Two numbers, not one
 *
 * The level is when to worry; the quantity is what to bring it back up to.
 * Ordering only up to the level means the next delivery arrives already at the
 * threshold, which produces a reorder alert the same week — so the form
 * refuses a target below the level and says why.
 */
export function ReorderLevels() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState<string | null>(null);

  const catalogue = useQuery({ queryKey: ['stock-catalogue'], queryFn: () => api.stockCatalogue() });
  const reorder = useQuery({ queryKey: ['stock-reorder'], queryFn: () => api.stockToReorder() });
  const overview = useQuery({ queryKey: ['stock-overview'], queryFn: () => api.stockOverview() });

  const save = useMutation({
    mutationFn: (rule: { code: string; reorderLevel: number; reorderQuantity: number }) =>
      api.setReorderRule(rule),
    onSuccess: async (_result, rule) => {
      setError(null);
      setJustSaved(rule.code);
      await queryClient.invalidateQueries({ queryKey: ['stock-reorder'] });
      // Long enough to notice, short enough not to linger over the next edit.
      setTimeout(() => setJustSaved(null), 1800);
    },
    onError: (e: Error) => setError(e.message),
  });

  /* What the store currently holds, so a level can be set against reality. */
  const store = overview.data?.locations.find((location) => location.kind === 'store');
  const held = new Map((store?.items ?? []).map((item) => [item.code, item.quantity]));

  /*
   * The rules that exist.
   *
   * `toReorder` only returns items that are *currently* low, so it cannot be
   * the source of "which items have a rule" — an item configured and fully
   * stocked would vanish from this screen and look unconfigured. The levels
   * come back on those rows though, so they are worth reading where present.
   */
  const configured = new Map(
    (reorder.data?.rows ?? []).map((row) => [row.code, row.reorderLevel]),
  );

  const items = (catalogue.data ?? []).filter((item) => {
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return (
      item.name.toLowerCase().includes(needle) ||
      item.code.toLowerCase().includes(needle) ||
      (item.aka ?? '').toLowerCase().includes(needle)
    );
  });

  return (
    <section className="card space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            When to reorder
            <HelpHint>
              Set these against how fast you actually get through each item and how long your
              supplier takes. An item with nothing set here is never reported as low — which is
              the honest answer for something nobody has an opinion about yet.
            </HelpHint>
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Counted in the store only. A van running low is a request to the store, not an order
            to a supplier.
          </p>
        </div>

        <label className="min-w-48 text-sm">
          <span className="sr-only">Search the stock list</span>
          <span className="relative block">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              className="input pl-9"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="ont, fibre, rj45"
            />
          </span>
        </label>
      </div>

      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      {catalogue.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the stock list
        </p>
      )}

      {catalogue.data && items.length === 0 && (
        <p className="text-sm text-muted-foreground">Nothing on the stock list matches that.</p>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs label-mono tracking-[0.12em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3 text-right">In the store</th>
                <th className="px-4 py-3 text-right">Tell me below</th>
                <th className="px-4 py-3 text-right">Order back up to</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <ReorderRow
                  key={item.code}
                  code={item.code}
                  name={item.name}
                  aka={item.aka}
                  unit={item.unit}
                  held={held.get(item.code) ?? 0}
                  level={configured.get(item.code) ?? null}
                  saving={save.isPending && save.variables?.code === item.code}
                  saved={justSaved === item.code}
                  onSave={(level, quantity) =>
                    save.mutate({ code: item.code, reorderLevel: level, reorderQuantity: quantity })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Leave a row blank to say nothing about it. Both numbers are needed for a rule to apply,
        and the target has to be above the level — otherwise the next delivery arrives already
        low.
      </p>
    </section>
  );
}

function ReorderRow({
  code,
  name,
  aka,
  unit,
  held,
  level,
  saving,
  saved,
  onSave,
}: {
  code: string;
  name: string;
  aka: string | null;
  unit: string;
  held: number;
  level: number | null;
  saving: boolean;
  saved: boolean;
  onSave: (level: number, quantity: number) => void;
}) {
  const [levelDraft, setLevelDraft] = useState(level === null ? '' : String(level));
  const [quantityDraft, setQuantityDraft] = useState('');

  /*
   * Saved on blur, and only when both numbers are present.
   *
   * A rule with a level and no target is not a rule the server will accept, so
   * sending it would produce an error message for something the person is
   * simply still in the middle of typing.
   */
  const commit = () => {
    const nextLevel = Number(levelDraft);
    const nextQuantity = Number(quantityDraft);
    if (!levelDraft.trim() || !quantityDraft.trim()) return;
    if (!Number.isFinite(nextLevel) || !Number.isFinite(nextQuantity)) return;
    onSave(nextLevel, nextQuantity);
  };

  const suffix = unit === 'metre' ? 'm' : '';

  return (
    <tr className="border-t border-border">
      <td className="px-4 py-2">
        {name}
        {aka && <span className="ml-1 text-xs text-muted-foreground">({aka})</span>}
        <span className="ml-2 text-xs text-muted-foreground">{code}</span>
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
        {held}
        {suffix}
      </td>
      <td className="px-4 py-2 text-right">
        <input
          className="input w-24 text-right tabular-nums"
          inputMode="decimal"
          value={levelDraft}
          placeholder="—"
          disabled={saving}
          onChange={(event) => setLevelDraft(event.target.value)}
          onBlur={commit}
          aria-label={`Reorder level for ${name}`}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          className="input w-24 text-right tabular-nums"
          inputMode="decimal"
          value={quantityDraft}
          placeholder="—"
          disabled={saving}
          onChange={(event) => setQuantityDraft(event.target.value)}
          onBlur={commit}
          aria-label={`Order back up to, for ${name}`}
        />
      </td>
      <td className="px-4 py-2 text-right">
        {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />}
        {saved && !saving && <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
      </td>
    </tr>
  );
}
